/**
 * A partida SOLO, simulada localmente.
 *
 * Por que sem servidor: o `first_prototype` do PULSO põe `servidor` no `exclude`,
 * e não é economia — é escopo. Objeto puxado responde ao input de quem puxa, logo
 * precisa de predição, e ao mesmo tempo é autoritativo e contestável. Isso é uma
 * TERCEIRA categoria de netcode, que o repo NEVER tem (hoje só existe avatar
 * próprio predito e os outros interpolados, `ADR-0011`). Ela é fatia própria, com
 * ADR — ver o fim de `docs/design/DESIGN-SOURCE.md`.
 *
 * O invariante 2 continua de pé e NEVER é contornado aqui: não existe autoridade
 * remota nesta partida, então não existe nada para o cliente mentir. No dia em que
 * existir, o dono do estado volta a ser a sala.
 *
 * Invariante 1: quem simula é o `step` de `@game/sim`, o MESMO do servidor.
 */
import { balance } from '@game/gamedata';
import {
  ACTION_MAGNET,
  addPlayer,
  createState,
  loadRoom,
  step,
  type BodyState,
  type GameState,
  type PlayerInput,
  type PlayerState,
} from '@game/sim';

/** Assento único do solo. */
export const SOLO_PLAYER_ID = 1;

/**
 * Passo fixo do sim, em ms. Sai do `tickRate` porque é dele que sai o `DT` de
 * `@game/sim`: simular com outro relógio seria física diferente da do servidor.
 */
const TICK_MS = 1000 / balance.tickRate;

/**
 * Teto de ticks recuperados por frame, mesma razão do caminho em rede: aba que
 * volta do segundo plano acumula segundos, e simular tudo de uma vez seria
 * teleporte na tela.
 */
const MAX_STEPS_PER_FRAME = 5;

export interface SoloIntent {
  readonly moveX: number;
  readonly moveZ: number;
  readonly magnet: boolean;
}

export class SoloSession {
  private readonly state: GameState;
  private readonly self: PlayerState;
  /** Argumento de `step`, reaproveitado: array novo por tick seria lixo a 30 Hz. */
  private readonly oneInput: PlayerInput[];
  private pendingMs = 0;
  private seq = 0;

  constructor(seed: number) {
    this.state = createState(seed);
    loadRoom(this.state, balance.protoRoom);
    this.self = addPlayer(this.state, SOLO_PLAYER_ID);
    this.oneInput = [
      { playerId: SOLO_PLAYER_ID, seq: 0, moveX: 0, moveZ: 0, actions: 0 },
    ];
  }

  get tick(): number {
    return this.state.tick;
  }

  get players(): readonly PlayerState[] {
    return this.state.players;
  }

  get bodies(): readonly BodyState[] {
    return this.state.bodies;
  }

  /** Corpo magnetizado agora; 0 = campo desligado. */
  get magnetTargetId(): number {
    return this.self.magnetTargetId;
  }

  /**
   * Avança o tempo em passos FIXOS. Devolve quantos ticks rodaram — zero é
   * resposta legítima em frame curto, e quem desenha usa isso para não redesenhar
   * um mundo que NEVER mudou.
   */
  advance(deltaMs: number, intent: SoloIntent): number {
    if (!Number.isFinite(deltaMs) || deltaMs <= 0) return 0;
    this.pendingMs += deltaMs;

    let steps = 0;
    while (this.pendingMs >= TICK_MS && steps < MAX_STEPS_PER_FRAME) {
      // Subtrai em vez de zerar: zerar jogaria fora o resto do frame, e o jogo
      // andaria mais devagar que o relógio em aparelho que NEVER fecha 60 fps.
      this.pendingMs -= TICK_MS;
      steps += 1;
      this.seq += 1;

      const input = this.oneInput[0]!;
      input.seq = this.seq;
      input.moveX = intent.moveX;
      input.moveZ = intent.moveZ;
      input.actions = intent.magnet ? ACTION_MAGNET : 0;
      step(this.state, this.oneInput);
    }
    // Excedente além de um passo é atraso que NEVER vai ser recuperado.
    if (this.pendingMs > TICK_MS) this.pendingMs = 0;
    return steps;
  }
}
